import React, { useEffect, useRef, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { CheckCircle, XCircle, ArrowRight } from "@phosphor-icons/react";
import { useAuth } from "../context/AuthContext";
import BookingQR from "../components/BookingQR";

const API = process.env.REACT_APP_BACKEND_URL;
const MAX_ATTEMPTS = 12;          // 12 × 2.5s ≈ 30s
const POLL_INTERVAL_MS = 2500;

const CheckoutReturn = () => {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const bookingId = params.get("booking_id");
  const { authUser } = useAuth();

  const [phase, setPhase] = useState("polling"); // polling | success | timeout | error
  const [info, setInfo] = useState(null);
  const attemptRef = useRef(0);

  useEffect(() => {
    if (!sessionId || !bookingId) {
      setPhase("error");
      return;
    }

    let cancelled = false;

    const tick = async () => {
      if (cancelled) return;
      attemptRef.current += 1;
      try {
        const r = await fetch(`${API}/api/checkout/status/${encodeURIComponent(sessionId)}?booking_id=${encodeURIComponent(bookingId)}`);
        const data = await r.json();
        if (!cancelled && data?.payment_status === "paid") {
          setInfo(data);
          setPhase("success");
          return;
        }
      } catch {
        /* network blip, try again */
      }
      if (cancelled) return;
      if (attemptRef.current >= MAX_ATTEMPTS) {
        setPhase("timeout");
        return;
      }
      setTimeout(tick, POLL_INTERVAL_MS);
    };

    tick();
    return () => { cancelled = true; };
  }, [sessionId, bookingId]);

  if (phase === "polling") {
    return (
      <div className="max-w-[600px] mx-auto px-6 py-24 text-center" data-testid="checkout-polling">
        <div className="inline-block w-12 h-12 border-2 border-sage-deep border-t-transparent rounded-full animate-spin mb-6" />
        <h1 className="font-heading text-3xl md:text-4xl font-light text-forest">Confirmando tu pago…</h1>
        <p className="text-stone mt-3 text-sm">No cierres esta pestaña. Esto suele tardar unos segundos.</p>
        <p className="text-ash text-xs mt-6">Intento {attemptRef.current} / {MAX_ATTEMPTS}</p>
      </div>
    );
  }

  if (phase === "timeout") {
    return (
      <div className="max-w-[600px] mx-auto px-6 py-24 text-center" data-testid="checkout-timeout">
        <div className="inline-block mb-4 text-amber-600"><XCircle size={56} weight="duotone" /></div>
        <h1 className="font-heading text-3xl md:text-4xl font-light text-forest">El pago aún se está procesando</h1>
        <p className="text-stone mt-3 text-sm">
          A veces Stripe tarda algo más. Si pagaste, te llegará un email con el QR cuando se complete. Si no, vuelve a intentarlo.
        </p>
        <div className="flex gap-3 justify-center mt-8">
          <Link to="/actividades" className="border border-forest/15 px-6 py-3 uppercase tracking-wider text-sm hover:bg-white transition rounded-full text-forest">Seguir explorando</Link>
          {authUser && <Link to="/dashboard" className="bg-sage-deep text-paper px-6 py-3 uppercase tracking-wider text-sm font-medium hover:bg-forest transition rounded-full">Mis reservas</Link>}
        </div>
      </div>
    );
  }

  if (phase === "error" || !info) {
    return (
      <div className="max-w-[600px] mx-auto px-6 py-24 text-center" data-testid="checkout-error">
        <div className="inline-block mb-4 text-red-600"><XCircle size={56} weight="duotone" /></div>
        <h1 className="font-heading text-3xl md:text-4xl font-light text-forest">Faltan datos para verificar el pago</h1>
        <p className="text-stone mt-3 text-sm">Vuelve al catálogo y prueba de nuevo.</p>
        <Link to="/actividades" className="mt-6 inline-block bg-sage-deep text-paper px-6 py-3 uppercase tracking-wider text-sm font-medium hover:bg-forest transition rounded-full">Ir al catálogo</Link>
      </div>
    );
  }

  // success
  return (
    <div className="max-w-[700px] mx-auto px-6 py-20 text-center" data-testid="checkout-success">
      <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} className="inline-block mb-6">
        <CheckCircle size={80} weight="fill" className="text-sage-deep" />
      </motion.div>
      <h1 className="font-heading text-4xl md:text-5xl font-light leading-tight mb-3 text-forest">¡Reserva confirmada!</h1>
      <p className="text-stone mb-8">
        Te enviaremos el QR de acceso a {info.email_guest || authUser?.email}
      </p>
      <div className="bg-white border border-forest/10 rounded-2xl p-6 text-left space-y-3">
        <div className="flex justify-between"><span className="text-stone">Estado</span><span className="text-sage-deep font-semibold uppercase text-xs">{info.status}</span></div>
        <div className="flex justify-between"><span className="text-stone">Confirmación</span><span className="font-mono text-xs text-forest">{info.booking_id}</span></div>
      </div>
      {info.qr_token && (
        <BookingQR value={info.qr_token} className="mt-8" testId="checkout-qr" />
      )}
      <div className="flex gap-3 mt-8 justify-center">
        <Link to="/actividades" className="border border-forest/15 px-6 py-3 uppercase tracking-wider text-sm hover:bg-white transition rounded-full text-forest">Seguir explorando</Link>
        {authUser && (
          <Link to="/dashboard" className="bg-sage-deep text-paper px-6 py-3 uppercase tracking-wider text-sm font-medium hover:bg-forest transition rounded-full inline-flex items-center gap-2">
            Ver mis reservas <ArrowRight size={16} />
          </Link>
        )}
      </div>
    </div>
  );
};

export default CheckoutReturn;
